import { useState, useMemo } from "react";
import { X, Copy, Calendar } from "lucide-react";

/**
 * DuplicateModal — Shared modal for duplicating Events / Formations with
 * configurable date shift and recurrence count.
 *
 * Props:
 *  - title: source item title (e.g. "WineTech Tour 2026")
 *  - sourceDate: ISO date of the source (used to display nth-weekday helper)
 *  - kind: "event" | "formation" — only used for copy text
 *  - onClose: () => void
 *  - onConfirm: ({ shift, count }) => Promise<void>
 */
export default function DuplicateModal({ title, sourceDate, kind = "event", onClose, onConfirm }) {
  const [shift, setShift] = useState("1y");
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);

  // Compute the Nth weekday-of-month label (e.g. "3ème jeudi du mois")
  const nthWeekdayLabel = useMemo(() => {
    if (!sourceDate) return "";
    try {
      const d = new Date(sourceDate);
      if (isNaN(d.getTime())) return "";
      const day = d.getDate();
      const nth = Math.floor((day - 1) / 7) + 1;
      const NTH_LABELS = ["", "1er", "2ème", "3ème", "4ème", "5ème"];
      const WEEKDAYS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
      return `${NTH_LABELS[nth]} ${WEEKDAYS[d.getDay()]} du mois`;
    } catch { return ""; }
  }, [sourceDate]);

  const SHIFT_OPTIONS = [
    { v: "none", l: "📭 Sans date (à définir manuellement)" },
    { v: "1d", l: "+1 jour" },
    { v: "1w", l: "+1 semaine" },
    { v: "2w", l: "+2 semaines (bimensuel)" },
    { v: "4w", l: "+4 semaines" },
    { v: "1m", l: "+1 mois" },
    { v: "3m", l: "+3 mois (trimestriel)" },
    { v: "6m", l: "+6 mois (semestriel)" },
    { v: "1y", l: "+1 an (même date l'année suivante)" },
    { v: "nth_weekday", l: nthWeekdayLabel ? `📅 Même schéma (${nthWeekdayLabel} suivant)` : "📅 Même schéma (Nème jour de semaine du mois suivant)" },
  ];

  const previewDates = useMemo(() => {
    if (!sourceDate || shift === "none") return [];
    const src = new Date(sourceDate);
    if (isNaN(src.getTime())) return [];
    const out = [];
    for (let i = 1; i <= Math.min(count, 6); i++) {
      const d = new Date(src);
      if (shift === "1d") d.setDate(d.getDate() + i);
      else if (shift === "1w") d.setDate(d.getDate() + 7 * i);
      else if (shift === "2w") d.setDate(d.getDate() + 14 * i);
      else if (shift === "4w") d.setDate(d.getDate() + 28 * i);
      else if (shift === "1m") d.setMonth(d.getMonth() + i);
      else if (shift === "3m") d.setMonth(d.getMonth() + 3 * i);
      else if (shift === "6m") d.setMonth(d.getMonth() + 6 * i);
      else if (shift === "1y") d.setFullYear(d.getFullYear() + i);
      else if (shift === "nth_weekday") {
        const nth = Math.floor((src.getDate() - 1) / 7) + 1;
        const weekday = src.getDay();
        const target = new Date(src.getFullYear(), src.getMonth() + i, 1);
        const offset = (weekday - target.getDay() + 7) % 7;
        target.setDate(1 + offset + 7 * (nth - 1));
        target.setHours(src.getHours(), src.getMinutes());
        d.setTime(target.getTime());
      }
      out.push(d);
    }
    return out;
  }, [sourceDate, shift, count]);

  const confirm = async () => {
    setBusy(true);
    try { await onConfirm({ shift, count }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="duplicate-modal">
      <button onClick={onClose} className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" aria-label="Fermer" />
      <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between border-b border-neutral-100 pb-3">
          <div>
            <h2 className="text-lg font-bold text-navy"><Copy className="mr-1 inline h-5 w-5 text-wine-600" /> Dupliquer {kind === "event" ? "l'événement" : "la formation"}</h2>
            <p className="mt-0.5 text-xs text-neutral-500 line-clamp-1">{title}</p>
          </div>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" data-testid="duplicate-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Décalage de la date</label>
            <select value={shift} onChange={(e) => setShift(e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm" data-testid="duplicate-shift" disabled={busy}>
              {SHIFT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Nombre de copies (récurrence)</label>
            <div className="mt-1 flex items-center gap-3">
              <input type="range" min="1" max="12" value={count} onChange={(e) => setCount(parseInt(e.target.value) || 1)} className="flex-1 accent-wine-500" data-testid="duplicate-count-range" disabled={busy || shift === "none"} />
              <input type="number" min="1" max="24" value={count} onChange={(e) => setCount(Math.max(1, Math.min(24, parseInt(e.target.value) || 1)))} className="w-16 rounded-lg border border-neutral-200 px-2 py-1 text-center text-sm" data-testid="duplicate-count" disabled={busy} />
            </div>
            <p className="mt-1 text-[10px] text-neutral-500">{shift === "none" ? "Sans décalage : une seule copie sans date." : `Crée ${count} copie${count > 1 ? "s" : ""} en série (chacune décalée).`}</p>
          </div>

          {previewDates.length > 0 && (
            <div className="rounded-2xl border border-wine-100 bg-wine-50/30 p-3 text-xs">
              <div className="flex items-center gap-1.5 font-bold text-wine-700"><Calendar className="h-3.5 w-3.5" /> Aperçu des dates générées</div>
              <ul className="mt-2 space-y-0.5 text-neutral-700">
                {previewDates.map((d, i) => (
                  <li key={i} data-testid={`duplicate-preview-${i}`}>
                    <span className="font-mono text-[11px] text-neutral-400">#{i + 1}</span>{" "}
                    <strong>{d.toLocaleDateString("fr", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</strong>{" "}
                    <span className="text-neutral-500">{d.toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })}</span>
                  </li>
                ))}
                {count > 6 && <li className="italic text-neutral-400">… +{count - 6} autre(s)</li>}
              </ul>
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2 border-t border-neutral-100 pt-4">
          <button onClick={onClose} disabled={busy} className="rounded-full border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-50">Annuler</button>
          <button onClick={confirm} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="duplicate-confirm">
            <Copy className="h-4 w-4" /> {busy ? "Duplication…" : `Dupliquer (${count})`}
          </button>
        </div>
      </div>
    </div>
  );
}
